// The category-pod style panel: the pod tile, its label, the row that wraps them, and the pod's
// hover state, in ONE surface with a live example pinned above it.
//
// It replaces the Home editor's inline Pod / Label / Container Pivot panel. Same reasoning as the
// product card (see ProductCardStylePanel): the parts are one visual object, tabs hid two thirds of
// it behind a click, and `podHover` was reachable only by hand-editing the raw JSON even though the
// storefront has always emitted it as a scoped `:hover` rule.
//
// The groups themselves are the Storefront Styles panel's part accordions, one open at a time, each
// ending in the same "Advanced…" escape into the raw key/value CSS editor. (No named-style picker:
// none of the pod keys maps to a style element type, so styleTargetForConfigKey has nothing to
// offer them.)
//
// The panel is deliberately dumb: it owns no state and parses no config. The Home editor passes the
// four dicts out of its own config blob plus the matching updaters.

import React from 'react';
import { Panel, PanelType } from '@fluentui/react';
import { Css } from 'nano-css/addon/vcssom/cssToTree';
import { ISearchContainerStyle, ISearchElementStyle, ISearchTextStyle } from './searchPageConfig';
import { advancedReplacementPatch, AdvancedStyleButton, elementStyleFrom, ElementPropertyRows, StyleGroupAccordion, textStyleFrom } from './styleControls';
import { resolveHoverPart, STYLE_PART_ICONS } from './stylesConfig';
import StyleExampleFrame from './StyleExampleFrame';
import StorefrontSectionAdvancedStyleEditorModal from '../StorefrontSectionAdvancedStyleEditorModal';
import './StorefrontEditor.css';

// ISearchTextStyle / ISearchContainerStyle are narrower than the ISearchElementStyle the generic
// rows speak, so the dicts don't structurally interchange in either direction, even though at
// RUNTIME they are all open camelCase CSS bags with an index signature. One loose cast at the
// boundary, in both directions, exactly as ProductCardStylePanel does it.
const loose = (dict?: { [prop: string]: any }): any => dict;

// A flat monitor-and-keyboard illustration standing in for a category thumbnail. Full color (unlike
// the nav pod's masked icon, category pods render a plain <img>), flat shapes only so it stays
// legible at the 48px the stylesheet draws it at. encodeURIComponent, not base64, so the source
// stays readable and editable in place.
const SAMPLE_IMAGE_SVG = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">
  <rect x="8" y="10" width="48" height="34" rx="4" fill="#faf9f8" stroke="#c8c6c4" stroke-width="2.5"/>
  <rect x="13" y="15" width="38" height="24" rx="2" fill="#eef3f9"/>
  <rect x="13" y="15" width="38" height="6" rx="2" fill="#0f6cbd" opacity="0.9"/>
  <rect x="18" y="26" width="20" height="3" rx="1.5" fill="#c8c6c4"/>
  <rect x="18" y="32" width="28" height="3" rx="1.5" fill="#d9d7d5"/>
  <rect x="20" y="48" width="24" height="5" rx="2.5" fill="#d2d0ce"/>
</svg>`;

const SAMPLE_IMAGE = `data:image/svg+xml;utf8,${encodeURIComponent(SAMPLE_IMAGE_SVG)}`;

// The pod exactly as the Home canvas and the live store draw it (same classes, same dark variant,
// same inline image sizing the category editor applies), with the config's styles on top.
const CategoryPodExample: React.FC<{
    pod?: ISearchElementStyle;
    podName?: ISearchTextStyle;
    hoverStyle?: React.CSSProperties;
    isDark?: boolean;
}> = (props) => {
    const [hovered, setHovered] = React.useState(false);

    // Simulated the same way as the product card's: the storefront emits this as a `:hover` rule,
    // which an inline style can't express, so it's merged over the pod while the pointer is in
    // the frame.
    const hasHover = !!props.hoverStyle && Object.keys(props.hoverStyle).length > 0;
    const podStyle: React.CSSProperties = {
        ...elementStyleFrom(props.pod),
        ...((hasHover && hovered) ? props.hoverStyle : {}),
    };

    const caption = hasHover
        ? 'Example pod, updates as you edit · point at the pod to preview hover styling'
        : 'Example pod, updates as you edit';

    return (
        <StyleExampleFrame isDark={props.isDark} caption={caption} onHoverChange={setHovered}>
            <div
                className={`storefront-category-pod-button${props.isDark ? ' storefront-category-pod-button-dark' : ''}`}
                style={podStyle}
            >
                <img
                    src={SAMPLE_IMAGE}
                    alt=''
                    style={{ maxWidth: '100%', maxHeight: 100, objectFit: 'contain', filter: props.isDark ? 'invert(1)' : undefined }}
                />
                <div className='storefront-category-pod-name' style={textStyleFrom(props.podName)}>Laptops</div>
            </div>
        </StyleExampleFrame>
    );
};

export interface ICategoryPodStylePanelProps {
    isOpen: boolean;
    onDismiss: () => void;
    pod?: ISearchElementStyle;
    onPodChange: (patch: Partial<ISearchElementStyle>) => void;
    podName?: ISearchTextStyle;
    onPodNameChange: (patch: Partial<ISearchTextStyle>) => void;
    podContainer?: ISearchContainerStyle;
    onPodContainerChange: (patch: Partial<ISearchContainerStyle>) => void;
    podHover?: ISearchElementStyle;
    onPodHoverChange: (patch: Partial<ISearchElementStyle>) => void;
    isDark?: boolean;
}

const CategoryPodStylePanel: React.FC<ICategoryPodStylePanelProps> = (props) => {
    // Exactly the renderer's pipeline: resolveHoverPart picks ONE ref (the hover dict's own, else
    // the resting pod's) and puts that preset's `hover` part UNDER the local podHover dict.
    const hoverStyle: React.CSSProperties =
        elementStyleFrom(loose(resolveHoverPart(props.podHover, props.pod)), 'hover');

    // One group open at a time, seeded on the pod itself (the part a merchant reaches for first) —
    // the same accordion behaviour as the Storefront Styles panel's part list, so the surface is a
    // short scannable list rather than four full property sets stacked end to end.
    const [openGroup, setOpenGroup] = React.useState('pod');
    const group = (key: string) => ({
        expanded: openGroup === key,
        onToggle: (expanded: boolean) => setOpenGroup(expanded ? key : ''),
    });

    // The raw key/value CSS editor, one entry per group. It hands back the WHOLE dict rather than a
    // patch (a key deleted there has to disappear), so each entry replaces its dict outright instead
    // of merging — the updaters above stay the merge path for the flat rows.
    const [advancedGroup, setAdvancedGroup] = React.useState<string | undefined>();
    const advanced: Record<string, { value?: Record<string, any>; replace: (css: Css) => void }> = {
        pod: { value: props.pod, replace: (css) => props.onPodChange(advancedReplacementPatch(props.pod, css)) },
        label: { value: props.podName, replace: (css) => props.onPodNameChange(advancedReplacementPatch(props.podName, css)) },
        container: { value: props.podContainer, replace: (css) => props.onPodContainerChange(advancedReplacementPatch(props.podContainer, css)) },
        hover: { value: props.podHover, replace: (css) => props.onPodHoverChange(advancedReplacementPatch(props.podHover, css)) },
    };

    return (
        <>
        <Panel
            isOpen={props.isOpen}
            onDismiss={props.onDismiss}
            headerText='Category Pods'
            type={PanelType.smallFixedFar}
            isBlocking={false}
            closeButtonAriaLabel='Close'
        >
            {/* Pinned above the groups so the effect of every row below is on screen without
                scrolling back to the preview canvas, and stays pinned while the groups scroll on
                viewports tall enough to afford it (sf-card-example-sticky, StorefrontEditor.css). */}
            <div className='sf-card-example-sticky' style={{ marginTop: 8 }}>
                <CategoryPodExample pod={props.pod} podName={props.podName} hoverStyle={hoverStyle} isDark={props.isDark} />
            </div>

            {/* No StyleRowsWidthProvider: this is a 340px smallFixedFar panel, the width the flat
                rows' default 180px control column was tuned for. */}
            <StyleGroupAccordion
                label='Pod'
                iconName={STYLE_PART_ICONS.card}
                tooltip='The pod tile itself: background, border, corner radius and shadow.'
                {...group('pod')}
            >
                <ElementPropertyRows value={props.pod} settingKeyPrefix='storefront.categoryPod.pod' onChange={props.onPodChange} />
                <AdvancedStyleButton onClick={() => setAdvancedGroup('pod')} />
            </StyleGroupAccordion>

            <StyleGroupAccordion
                label='Label'
                iconName={STYLE_PART_ICONS.label}
                tooltip='The category name under the image. Font settings here style that line only.'
                {...group('label')}
            >
                <ElementPropertyRows value={loose(props.podName)} settingKeyPrefix='storefront.categoryPod.title' onChange={(patch) => props.onPodNameChange(loose(patch))} />
                <AdvancedStyleButton onClick={() => setAdvancedGroup('label')} />
            </StyleGroupAccordion>

            <StyleGroupAccordion
                label='Container'
                iconName={STYLE_PART_ICONS.band}
                tooltip='The row that wraps the pods, not the pods themselves.'
                {...group('container')}
            >
                <ElementPropertyRows value={loose(props.podContainer)} settingKeyPrefix='storefront.categoryPod.container' onChange={(patch) => props.onPodContainerChange(loose(patch))} />
                <AdvancedStyleButton onClick={() => setAdvancedGroup('container')} />
            </StyleGroupAccordion>

            <StyleGroupAccordion
                label='Hover'
                iconName={STYLE_PART_ICONS.hover}
                tooltip="Applied while the shopper's pointer is over the pod - the storefront emits it as a scoped :hover rule with a short transition."
                {...group('hover')}
            >
                <ElementPropertyRows value={props.podHover} settingKeyPrefix='storefront.categoryPod.hover' onChange={props.onPodHoverChange} />
                <AdvancedStyleButton onClick={() => setAdvancedGroup('hover')} />
            </StyleGroupAccordion>
        </Panel>

        {/* Rendered once, outside the accordions: the raw CSS editor for whichever group asked. */}
        {advancedGroup && advanced[advancedGroup] && (
            <StorefrontSectionAdvancedStyleEditorModal
                css={(advanced[advancedGroup].value ?? {}) as Css}
                displayModal
                closer={() => setAdvancedGroup(undefined)}
                onChange={(css) => advanced[advancedGroup].replace(css)}
            />
        )}
        </>
    );
};

export default CategoryPodStylePanel;
